import { useQueryClient } from '@tanstack/react-query';
import { Loader2 } from 'lucide-react';
import { useState } from 'react';
import { toast } from 'sonner';

import type { PacienteResponse } from '@/api/generated/model';
import {
  getPacientesControllerListarQueryKey,
  usePacientesControllerVincularTags,
} from '@/api/generated/pacientes/pacientes';
import { useTagsDePacienteControllerListar } from '@/api/generated/tags-de-paciente/tags-de-paciente';
import { ApiError } from '@/api/http-client';
import { Button } from '@/shared/ui/button';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/shared/ui/dialog';

interface Props {
  aberto: boolean;
  aoFechar: () => void;
  paciente: PacienteResponse;
}

/**
 * Marca/desmarca as tags DESTE paciente. Criar, renomear ou excluir uma tag
 * não é daqui — é do `GerenciarTagsDialogo`, aberto ao lado de "Favoritos" no
 * topo de `/pacientes`: cadastrar uma tag é sobre o ambiente inteiro, não
 * sobre o paciente que a pessoa está olhando agora.
 */
export function VincularTagsDialogo({ aberto, aoFechar, paciente }: Props) {
  const clienteDeQuery = useQueryClient();

  const [selecionadas, setSelecionadas] = useState<Set<string>>(
    new Set(paciente.tags.map((tag) => tag.id)),
  );

  const { data: pagina, isPending: carregando } = useTagsDePacienteControllerListar({
    porPagina: 100,
  });
  const tags = pagina?.itens ?? [];

  const vincular = usePacientesControllerVincularTags();

  function fechar() {
    aoFechar();
  }

  function alternar(id: string) {
    setSelecionadas((atual) => {
      const proximo = new Set(atual);
      if (proximo.has(id)) proximo.delete(id);
      else proximo.add(id);
      return proximo;
    });
  }

  async function salvar() {
    try {
      await vincular.mutateAsync({ id: paciente.id, data: { tagIds: [...selecionadas] } });
      void clienteDeQuery.invalidateQueries({ queryKey: getPacientesControllerListarQueryKey() });
      toast.success('Tags atualizadas.');
      fechar();
    } catch (erro) {
      toast.error(erro instanceof ApiError ? erro.message : 'Não conseguimos salvar as tags.');
    }
  }

  return (
    <Dialog
      open={aberto}
      onOpenChange={(estado) => {
        if (!estado) fechar();
      }}
    >
      <DialogContent>
        <DialogHeader>
          <DialogTitle>Tags de {paciente.nome}</DialogTitle>
          <DialogDescription>
            Marque as tags deste paciente.{' '}
            {tags.length === 0 && !carregando && 'Nenhuma tag criada neste ambiente ainda.'}
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-1 max-h-80 overflow-y-auto">
          {carregando && (
            <p className="flex items-center gap-2 text-sm text-muted-foreground py-4">
              <Loader2 className="w-3.5 h-3.5 animate-spin" />
              Carregando tags...
            </p>
          )}

          {tags.map((tag) => (
            <label
              key={tag.id}
              className="flex items-center gap-2 rounded-md px-2 py-1.5 hover:bg-muted/50 cursor-pointer"
            >
              <input
                type="checkbox"
                checked={selecionadas.has(tag.id)}
                onChange={() => alternar(tag.id)}
                className="size-4 shrink-0 accent-brand"
              />
              <span
                className="inline-block size-2.5 rounded-full shrink-0"
                style={{ backgroundColor: tag.cor }}
              />
              <span className="flex-1 truncate text-sm text-foreground">{tag.nome}</span>
            </label>
          ))}
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={fechar}>
            Cancelar
          </Button>
          <Button disabled={vincular.isPending} onClick={() => void salvar()}>
            {vincular.isPending && <Loader2 className="w-4 h-4 animate-spin mr-2" />}
            Salvar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
